import React, { ReactElement, useState, useEffect } from 'react';
import styled from 'styled-components';
import { IconSize, CaretDownIcon, Dropdown, DropdownItem } from '@lifechurch/react-ion';
import BeamerUpdates from './BeamerUpdates';
import { useTranslation } from 'react-i18next';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

export const SupportBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${props => props.theme.colors.gray10};
  border-radius: 12px;
  padding-block-start: 4px;
  padding-block-end: 4px;
  padding-inline-start: 10px;
  padding-inline-end: 6px;
  height: 24px;
  cursor: pointer;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 ${props => props.theme.colors.action.default};
    }
    60% {
      box-shadow: 0 0 0 4px transparent;
    }
    100% {
      box-shadow: 0 0 0 4px transparent;
    }
  }

  > span {
    color: ${props => props.theme.colors.gray50};
    font-weight: ${props => props.theme.fontWeights[2]};
    font-size: ${props => props.theme.fontSizes[2]};
    line-height: ${props => props.theme.lineHeights[2]};
  }

  svg path {
    fill: ${props => props.theme.colors.gray50};
    opacity: 1;
  }

  &.hasUpdates {
    background-color: ${props => props.theme.colors.action.default};
    animation: pulse 2s infinite;
    animation-timing-function: ease-out;
    transition: all 0.25s ease-out;
    > span {
      color: white;
    }
    svg path {
      fill: white;
    }
  }

  &:hover,
  &:focus {
    background-color: ${props => props.theme.colors.action.light};
    > span {
      color: ${props => props.theme.colors.action.hover};
    }
    svg path {
      fill: ${props => props.theme.colors.action.hover};
    }
  }
`;

const Feedback: React.FC = () => {
  const [updateCount, setUpdateCount] = useState(0);
  const { t } = useTranslation('common');

  useEffect(() => {
    window.beamer_config = {
      product_id: publicRuntimeConfig.BEAMER_PRODUCT_ID,
      selector: '#beamer-button',
      alert: false,
      counter: false,
      lazy: true,
      ignore_boosted_announcements: true,
      callback: (newPostCount: number) => {
        setUpdateCount(newPostCount);
      },
    };

    const initBeamer = (): void => {
      if (window.Beamer && typeof window.Beamer.init === 'function') {
        window.Beamer.init();
      } else {
        setTimeout(initBeamer, 100);
      }
    };

    initBeamer();
  }, []);

  function markAllSeen(): void {
    setUpdateCount(0);
  }

  const getSupportText = (): string =>
    updateCount === 1
      ? `${updateCount} ${t('update')}`
      : updateCount > 1
      ? `${updateCount} ${t('updates')}`
      : t('support');

  const ReferenceContent: ReactElement = (
    <SupportBox className={updateCount > 0 ? 'hasUpdates' : ''} data-testid='feedback-button'>
      <span id='beamer-button' />
      <span>{getSupportText()}</span>
      <CaretDownIcon size={IconSize.SM} />
    </SupportBox>
  );

  const PopperContent: ReactElement = (
    <>
      <BeamerUpdates handleClick={markAllSeen} hasUpdates={updateCount > 0} />
      <DropdownItem href='https://status.online.church/' rel='noopener noreferrer' target='_blank'>
        {t('platform_status')}
      </DropdownItem>
      <DropdownItem
        data-testid='support'
        href='https://support.online.church/'
        rel='noopener noreferrer'
        target='_blank'
      >
        {t('get_support')}
      </DropdownItem>
      <DropdownItem
        data-testid='feedback'
        href='https://lifechurch.formstack.com/forms/church_feedback'
        rel='noopener noreferrer'
        target='_blank'
      >
        {t('give_feedback')}
      </DropdownItem>
    </>
  );

  return <Dropdown popperContent={PopperContent} referenceContent={ReferenceContent} />;
};

export default Feedback;
